/* ============================================================================
   Admin module — Access Requests panel.
   Compact table of public "request access" submissions, filterable by
   status (pending / approved / rejected). Approve creates the User and
   surfaces the generated temp password once (mirrors CreateUserDialog's
   invite UX); reject asks for a short reason. Wired to
   GET  /api/access-requests?status=<status>
   POST /api/access-requests/{id}/approve
   POST /api/access-requests/{id}/reject   { reason }
   ============================================================================ */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, isApiError } from '../../lib/api';
import Modal from './Modal';

type ReqStatus = 'pending' | 'approved' | 'rejected';

interface AccessRequestRow {
  id: number;
  name: string;
  email: string;
  requestedRoleCode: string;
  justification: string;
  status: ReqStatus;
  rejectReason: string | null;
  reviewedBy: number | null;
  reviewedAt: string | null;
  createdUserId: number | null;
  createdAt: string;
  emailAlreadyRegistered: boolean;
}

interface ApproveResult {
  id: number;
  name: string;
  email: string;
  roleCode: string;
  tempPassword: string;
}

const FILTERS: { key: ReqStatus; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

function ageLabel(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function AccessRequestsPanel() {
  const [status, setStatus] = useState<ReqStatus>('pending');
  const [rows, setRows] = useState<AccessRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rowBusy, setRowBusy] = useState<number | null>(null);
  const [rowError, setRowError] = useState<{ id: number; message: string } | null>(null);

  const [approveResult, setApproveResult] = useState<ApproveResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [rejecting, setRejecting] = useState<AccessRequestRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSaving, setRejectSaving] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);

  const load = useCallback(async (s: ReqStatus) => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiGet<AccessRequestRow[]>(`/api/access-requests?status=${s}`);
      setRows(Array.isArray(r) ? r : []);
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Failed to load access requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(status);
  }, [load, status]);

  const sorted = useMemo(
    () => [...rows].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [rows],
  );

  async function approve(row: AccessRequestRow) {
    setRowBusy(row.id);
    setRowError(null);
    try {
      const res = await apiPost<ApproveResult>(`/api/access-requests/${row.id}/approve`, {});
      setApproveResult(res);
      await load(status);
    } catch (e) {
      setRowError({ id: row.id, message: isApiError(e) ? e.message : 'Could not approve this request.' });
    } finally {
      setRowBusy(null);
    }
  }

  function openReject(row: AccessRequestRow) {
    setRejectReason('');
    setRejectError(null);
    setRejecting(row);
  }

  async function submitReject() {
    if (!rejecting) return;
    if (!rejectReason.trim()) {
      setRejectError('A reason is required.');
      return;
    }
    setRejectSaving(true);
    setRejectError(null);
    try {
      await apiPost(`/api/access-requests/${rejecting.id}/reject`, { reason: rejectReason.trim() });
      setRejecting(null);
      await load(status);
    } catch (e) {
      setRejectError(isApiError(e) ? e.message : 'Could not reject this request.');
    } finally {
      setRejectSaving(false);
    }
  }

  function copyPassword() {
    if (!approveResult) return;
    navigator.clipboard?.writeText(approveResult.tempPassword).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => undefined,
    );
  }

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className={`chip ${status === f.key ? 'chip-active' : ''}`}
            onClick={() => setStatus(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="card" style={{ display: 'grid', placeItems: 'center', padding: 48 }}>
          <div className="spinner" />
        </div>
      ) : error ? (
        <div className="card empty">
          <h3>Couldn't load access requests</h3>
          <p className="muted">{error}</p>
        </div>
      ) : sorted.length === 0 ? (
        <div className="card empty">
          <h3>No {status} requests</h3>
          <p className="muted">
            {status === 'pending'
              ? 'New access requests will show up here for review.'
              : `Nothing has been ${status} yet.`}
          </p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-compact">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Requested role</th>
                <th>Justification</th>
                <th>Age</th>
                {status === 'pending' && <th style={{ textAlign: 'right' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => {
                const busy = rowBusy === row.id;
                return (
                  <tr key={row.id}>
                    <td style={{ fontWeight: 600 }}>{row.name}</td>
                    <td className="mono">
                      {row.email}
                      {row.emailAlreadyRegistered && (
                        <span className="pill pill-warning" style={{ marginLeft: 6 }} title="A user with this email already exists">
                          already registered
                        </span>
                      )}
                    </td>
                    <td>{row.requestedRoleCode || '—'}</td>
                    <td className="muted" style={{ maxWidth: 320 }} title={row.justification}>
                      {row.justification.length > 90
                        ? `${row.justification.slice(0, 90)}…`
                        : row.justification}
                    </td>
                    <td className="mono">{ageLabel(row.createdAt)}</td>
                    {status === 'pending' && (
                      <td style={{ textAlign: 'right' }}>
                        <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                          <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => openReject(row)}>
                            Reject
                          </button>
                          <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => approve(row)}>
                            {busy ? '…' : 'Approve'}
                          </button>
                        </div>
                        {rowError?.id === row.id && (
                          <div className="error-text" style={{ marginTop: 4 }}>
                            {rowError.message}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Reject: small reason prompt. */}
      {rejecting && (
        <Modal
          title={`Reject request from ${rejecting.name}`}
          onClose={() => setRejecting(null)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setRejecting(null)} disabled={rejectSaving}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={submitReject} disabled={rejectSaving}>
                {rejectSaving ? 'Rejecting…' : 'Reject request'}
              </button>
            </>
          }
        >
          {rejectError && (
            <div className="error-text" style={{ marginBottom: 14 }}>
              {rejectError}
            </div>
          )}
          <div className="field" style={{ marginBottom: 4 }}>
            <label htmlFor="ar-reject-reason">Reason</label>
            <textarea
              id="ar-reject-reason"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Role not applicable to this requester's department."
              autoFocus
            />
          </div>
        </Modal>
      )}

      {/* Approve: temp password shown once. */}
      {approveResult && (
        <Modal
          title="Access request approved"
          onClose={() => setApproveResult(null)}
          footer={
            <button className="btn btn-primary" onClick={() => setApproveResult(null)}>
              Done
            </button>
          }
        >
          <p style={{ marginTop: 0 }}>
            <strong>{approveResult.name}</strong> ({approveResult.email}) was created as{' '}
            <strong>{approveResult.roleCode}</strong>.
          </p>
          <div className="card" style={{ background: 'var(--paper-2)', padding: 16 }}>
            <p className="eyebrow" style={{ marginTop: 0 }}>
              Temporary password — shown once
            </p>
            <div className="row" style={{ gap: 10, alignItems: 'center' }}>
              <code
                className="mono"
                style={{
                  flex: 1,
                  fontSize: 'var(--fs-md)',
                  padding: '10px 12px',
                  background: 'var(--paper)',
                  border: '1px solid var(--line-strong)',
                  borderRadius: 'var(--r-md)',
                  userSelect: 'all',
                }}
              >
                {approveResult.tempPassword}
              </code>
              <button className="btn btn-navy btn-sm" onClick={copyPassword}>
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="muted" style={{ marginBottom: 0, marginTop: 10 }}>
              Share this with the user securely. It cannot be shown again — you can only issue a new one.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
}
